import * as React from 'react'
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react'
import {
  branchesApi,
  useAppDispatch,
  useClearHistorianMutation,
  useClearNeo4jMutation,
  useClearPostgresMutation,
  useSwitchBranch,
  type ClearHistorianResult,
  type ClearNeo4jResult,
  type ClearPostgresResult
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { ConfirmDialog } from '../../ConfigurePage/components/DeleteConfirmDialog'

function getErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return 'Unknown error.'
  const obj = error as Record<string, unknown>
  if (obj['data'] && typeof obj['data'] === 'object') {
    const data = obj['data'] as Record<string, unknown>
    if (typeof data['message'] === 'string') return data['message']
    if (typeof data['error'] === 'string') return data['error']
  }
  if (typeof obj['error'] === 'string') return obj['error']
  if (typeof obj['message'] === 'string') return obj['message']
  return 'Unknown error.'
}

export function ClearDatabasesCard({ embedded = false }: { embedded?: boolean }) {
  const dispatch = useAppDispatch()
  const { switchBranch } = useSwitchBranch()

  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [result, setResult] = React.useState<{
    neo4j: ClearNeo4jResult
    postgres: ClearPostgresResult
    historian: ClearHistorianResult
  } | null>(null)
  const [error, setError] = React.useState('')

  const [clearNeo4j, neo4jState] = useClearNeo4jMutation()
  const [clearPostgres, postgresState] = useClearPostgresMutation()
  const [clearHistorian, historianState] = useClearHistorianMutation()
  const isClearing = neo4jState.isLoading || postgresState.isLoading || historianState.isLoading

  const runClear = React.useCallback(async () => {
    setError('')
    setResult(null)
    try {
      // The Postgres truncate destroys every branch along with the registry
      // tables, so the only graph that survives a clear is live main. Land on
      // the baseline first so the Neo4j wipe targets main and the UI is not
      // left holding a branch id the reset is about to delete.
      await switchBranch(undefined)
      // Wipe Neo4j first: if the graph database is unreachable the call fails
      // before Postgres is touched, so a recoverable error leaves Postgres
      // intact rather than half-cleared. Historian is last: a Timescale
      // failure still leaves the control-plane wipe committed, and the
      // endpoint is a no-op skip when HISTORIAN_POSTGRES_* is unset.
      const neo4j = await clearNeo4j().unwrap()
      const postgres = await clearPostgres().unwrap()
      const historian = await clearHistorian().unwrap()
      // The Postgres clear rebuilt the branch registry server-side (fresh
      // main row, fresh root commit); drop anything fetched mid-clear.
      dispatch(branchesApi.util.invalidateTags(['Branches', 'Commits']))
      setResult({ neo4j, postgres, historian })
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setConfirmOpen(false)
    }
  }, [clearHistorian, clearNeo4j, clearPostgres, dispatch, switchBranch])

  const body = (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <h3 className="text-sm font-semibold text-st-down">Clear databases</h3>
        <p className="text-xs text-ink-3">
          Permanently wipe the <span className="font-mono font-medium">main</span> Neo4j graph,
          delete every branch and its database, truncate every operational Postgres table (ingest
          jobs, agent runs, insights, dashboards, …), and delete this organization's historian
          Timescale rows when that store is configured. Branching is re-initialized from the emptied
          graph.
        </p>
      </div>

      <div
        role="alert"
        className="flex items-start gap-2 rounded-md border border-st-down/30 bg-st-down-bg px-3 py-2 text-xs text-st-down"
      >
        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          This deletes data from Neo4j, Postgres, and historian Timescale at once. There is no undo.
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          className="border-st-down/40 bg-st-down-bg text-st-down hover:bg-st-down-bg hover:text-st-down"
          onClick={() => {
            setError('')
            setConfirmOpen(true)
          }}
          disabled={isClearing}
        >
          {isClearing ? <Loader2 className="animate-spin" /> : <Trash2 />}
          {isClearing ? 'Clearing…' : 'Clear databases'}
        </Button>
      </div>

      {error ? (
        <div role="alert" className="rounded-md bg-st-down-bg px-2.5 py-1.5 text-xs text-st-down">
          {error}
        </div>
      ) : null}

      {result ? (
        <p className="text-xs text-ink-2">
          Cleared {result.neo4j.nodesDeleted} node
          {result.neo4j.nodesDeleted === 1 ? '' : 's'} and {result.neo4j.relationshipsDeleted}{' '}
          relationship{result.neo4j.relationshipsDeleted === 1 ? '' : 's'} from branch{' '}
          <span className="font-mono">{result.neo4j.branch}</span>, and truncated{' '}
          {result.postgres.tablesTruncated} Postgres table
          {result.postgres.tablesTruncated === 1 ? '' : 's'}
          {result.historian.skipped
            ? '. Historian was not configured.'
            : `, and deleted ${result.historian.rowsDeleted} historian row${
                result.historian.rowsDeleted === 1 ? '' : 's'
              }.`}
        </p>
      ) : null}
    </div>
  )

  const dialog = (
    <ConfirmDialog
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
      title="Clear all databases?"
      description="This permanently deletes the main Neo4j graph, every branch, all operational Postgres data, and this organization's historian Timescale rows when that store is configured, then re-initializes branching from the empty graph. This cannot be undone."
      confirmLabel="Clear databases"
      destructive
      busy={isClearing}
      onConfirm={() => void runClear()}
    />
  )

  if (embedded) {
    return (
      <>
        <div className="rounded-xl border border-st-down/30 bg-card px-4 py-4 shadow-sm">
          {body}
        </div>
        {dialog}
      </>
    )
  }

  return (
    <Card className="gap-3 border-st-down/30 py-4">
      <CardContent className="flex flex-col gap-3">{body}</CardContent>
      {dialog}
    </Card>
  )
}
